import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { userApi } from '../../services/api';
import { consumeLinkedInOAuthState, consumeLinkedInRedirectUri } from '../../services/linkedinAuth';
import { IDENTITY_PROVIDERS, markLinkedInSession } from '../../config/identityProviders';
import { LinkedInSignInAction, LinkedInSignInResponse } from '../../types';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import LoadingOverlay from '../../components/UI/LoadingOverlay';

const ACTION = {
  None: 0,
  CreateProfile: 1,
  ChangeLocalToLinkedIn: 2,
  ChangeOtherToLinkedIn: 3,
} as const;

function isAction(action: LinkedInSignInAction | undefined, name: keyof typeof ACTION): boolean {
  return action === name || action === ACTION[name];
}

const LinkedInCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { completeLoginWithToken, beginExternalProfileSetup } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isWorking, setIsWorking] = useState(true);
  const [linkTicket, setLinkTicket] = useState<string | null>(null);
  const [linkMessage, setLinkMessage] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;

    const oauthError = searchParams.get('error');
    if (oauthError) {
      setError(searchParams.get('error_description') || 'LinkedIn sign-in was cancelled or failed.');
      setIsWorking(false);
      return;
    }

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const expectedState = consumeLinkedInOAuthState();

    if (!code) {
      setError('LinkedIn did not return an authorization code.');
      setIsWorking(false);
      return;
    }

    if (!state || !expectedState || state !== expectedState) {
      setError('LinkedIn sign-in could not be verified. Please try again.');
      setIsWorking(false);
      return;
    }

    const signIn = async () => {
      try {
        const response = await userApi.linkedInSignIn(code, consumeLinkedInRedirectUri());
        await handleSignInResponse(response.data);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'LinkedIn sign-in failed. Please try again.';
        setError(message);
        setIsWorking(false);
      }
    };

    void signIn();
    // OAuth callback should run once; LinkedIn authorization codes are single-use.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSignInResponse = async (data: LinkedInSignInResponse) => {
    markLinkedInSession(data.linkedInAccessToken);

    if (isAction(data.actionRequired, 'CreateProfile') && data.token && data.email) {
      beginExternalProfileSetup(
        data.token,
        data.email,
        data.givenName,
        data.mobile,
        data.linkedInAccessToken,
        IDENTITY_PROVIDERS.LinkedIn,
        data.pictureUrl
      );
      return;
    }

    if (
      (isAction(data.actionRequired, 'ChangeLocalToLinkedIn') || isAction(data.actionRequired, 'ChangeOtherToLinkedIn'))
      && data.linkTicket
    ) {
      setLinkTicket(data.linkTicket);
      setLinkMessage(data.message || 'An account with this email already exists. Link it to LinkedIn to continue.');
      setIsWorking(false);
      return;
    }

    if (data.token) {
      await completeLoginWithToken(data.token, data.idUserProfile);
      return;
    }

    setError(data.message || 'LinkedIn sign-in failed. Please try again.');
    setIsWorking(false);
  };

  const confirmLink = async () => {
    if (!linkTicket) {
      return;
    }
    setIsConfirming(true);
    setError(null);
    try {
      const response = await userApi.confirmLinkedInLink(linkTicket);
      if (response.data.token) {
        await completeLoginWithToken(response.data.token, response.data.idUserProfile);
        return;
      }
      setError(response.data.message || 'Unable to link this LinkedIn account.');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unable to link this LinkedIn account.';
      setError(message);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className="min-h-[calc(100dvh-4.5rem)] flex flex-col justify-center py-10 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <h2 className="text-3xl font-extrabold text-gray-900">
            {linkTicket ? 'Link your LinkedIn account' : 'Signing in with LinkedIn'}
          </h2>
        </motion.div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <Card className="py-8 px-4 sm:px-10 relative">
          {isWorking && <LoadingOverlay />}
          {error && (
            <div className="mb-4 p-3 bg-error-50 text-error-600 text-sm rounded-md border border-error-200">
              {error}
            </div>
          )}
          {linkTicket ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">{linkMessage}</p>
              <Button type="button" fullWidth isLoading={isConfirming} onClick={confirmLink}>
                Link and continue
              </Button>
              <Link to="/login">
                <Button type="button" variant="outline" fullWidth>
                  Cancel
                </Button>
              </Link>
            </div>
          ) : error ? (
            <Link to="/login">
              <Button type="button" variant="outline" fullWidth>
                Back to sign in
              </Button>
            </Link>
          ) : (
            <p className="text-sm text-gray-600 text-center">Completing LinkedIn sign-in…</p>
          )}
        </Card>
      </div>
    </div>
  );
};

export default LinkedInCallback;
